import { z } from 'zod'

import { groupOrderPayloadSchema } from '@/features/group/schema'
import { toPagination } from '@/features/group/pagination'

/**
 * WEBLP-3185 `GET /v1/algo-pos-be/liotitip/order` — a flat list of orders across
 * all of the customer's verified groups (no group ID in the path). The response
 * has NO `data` wrapper, so the client must be called with `unwrapResponse: false`.
 * Each order reuses the existing group order shape (so the parsed `GroupOrder`
 * type stays identical and every order component keeps working).
 */

/** Stored counters summed across all verified groups, independent of filters/paging. */
const statusCountsSchema = z
  .object({
    unpaid: z.number().int().nonnegative().nullish(),
    billed: z.number().int().nonnegative().nullish(),
    in_process: z.number().int().nonnegative().nullish(),
    shipped: z.number().int().nonnegative().nullish(),
    completed: z.number().int().nonnegative().nullish(),
  })
  .nullish()
  .transform((counts) => ({
    unpaid: counts?.unpaid ?? 0,
    billed: counts?.billed ?? 0,
    inProcess: counts?.in_process ?? 0,
    shipped: counts?.shipped ?? 0,
    completed: counts?.completed ?? 0,
  }))

const filterGroupSchema = z
  .object({
    id: z.number().int().nullish(),
    external_id: z.string().nullish(),
    name: z.string().nullish(),
  })
  .transform((group) => ({
    id: group.external_id || String(group.id ?? ''),
    name: group.name || '',
  }))

const filtersSchema = z
  .object({
    groups: z.array(filterGroupSchema).nullish(),
    sources: z.array(z.string()).nullish(),
  })
  .nullish()
  .transform((filters) => ({
    groups: filters?.groups ?? [],
    sources: filters?.sources?.filter((source) => source.trim().length > 0) ?? [],
  }))

export const orderListPayloadSchema = z
  .object({
    orders: z.array(groupOrderPayloadSchema).nullish(),
    need_to_buy: z.array(z.unknown()).nullish(),
    pagination: z
      .object({
        page: z.number().int().nullish(),
        per_page: z.number().int().nullish(),
        total_pages: z.number().int().nullish(),
        total_count: z.number().int().nullish(),
      })
      .nullish(),
    filters: filtersSchema,
    status_counts: statusCountsSchema,
  })
  .transform((payload) => ({
    orders: payload.orders ?? [],
    needToBuyCount: payload.need_to_buy?.length ?? 0,
    pagination: toPagination(payload.pagination, payload.orders?.length ?? 0),
    filters: payload.filters,
    statusCounts: payload.status_counts,
  }))

export type OrderList = z.infer<typeof orderListPayloadSchema>
export type OrderListStatusCounts = OrderList['statusCounts']
export type OrderListFilterGroup = OrderList['filters']['groups'][number]

export interface OrderListFilters {
  page?: number
  perPage?: number
  /** Comma-joined group external IDs. */
  groups?: string
  /** Comma-joined order sources (MANUAL, WHATSAPP). */
  sources?: string
  /** Comma-joined order statuses. */
  orderStatus?: string
  /** A single invoice status (not comma-separated). */
  invoiceStatus?: string
  search?: string
}

/** The five dashboard stat tiles, read straight from the API's status_counts. */
export function orderListStatusTotal(counts: OrderListStatusCounts): number {
  return counts.unpaid + counts.billed + counts.inProcess + counts.shipped + counts.completed
}
